<template>
  <HrPageLayout
    :title="pageTitle"
    :subtitle="pageSubtitle"
    icon="📋"
    :breadcrumb-items="[{ label: roleSectionLabel }, { label: pageTitle }]"
  >
    <!-- Alert Notification System -->
    <transition name="wd-alert-fade">
      <div v-if="alertMessage" class="wd-alert" :class="alertClass">
        <div class="wd-alert-icon">
          <i :class="alertIcon"></i>
        </div>
        <div class="wd-alert-content">
          <div class="wd-alert-message">{{ alertMessage }}</div>
        </div>
        <button class="wd-alert-close" @click="closeAlert" aria-label="Close alert">
          <i class="bx bx-x"></i>
        </button>
      </div>
    </transition>

    <div class="director-module">
      <!-- Modern Tabs -->
      <div class="wd-tabs-container">
        <div class="wd-tabs">
          <button 
            type="button" 
            class="wd-tab" 
            :class="{ 'wd-tab-active': activeTab === 'list' }" 
            @click="activeTab = 'list'; loadDeclarations()"
          >
            <i class="bx bx-list-ul"></i>
            <span>{{ listTabLabel }}</span>
            <span v-if="declarations.length" class="wd-tab-badge">{{ declarations.length }}</span>
          </button>
          <button 
            type="button" 
            class="wd-tab" 
            :class="{ 'wd-tab-active': activeTab === 'summary' }" 
            @click="activeTab = 'summary'; loadSummary()"
          >
            <i class="bx bx-bar-chart-alt-2"></i>
            <span>Summary</span>
          </button>
          <button 
            type="button" 
            class="wd-tab" 
            :class="{ 'wd-tab-active': activeTab === 'reports' }" 
            @click="activeTab = 'reports'"
          >
            <i class="bx bx-file"></i>
            <span>Reports</span>
          </button>
        </div>
      </div>

      <!-- List Tab -->
      <div v-show="activeTab === 'list'" class="wd-card">
        <div class="wd-card-header">
          <div class="wd-card-title-group">
            <h3 class="wd-card-title">
              <i class="bx bx-spreadsheet"></i>
              Declarations
            </h3>
            <span class="wd-badge-count">{{ declarations.length }} record{{ declarations.length !== 1 ? 's' : '' }}</span>
          </div>
          <div class="wd-card-actions">
            <button v-if="isApprover && allowManualEntry" type="button" class="wd-btn wd-btn-primary" @click="openDeclarationModal()">
              <i class="bx bx-plus"></i>
              Add Declaration
            </button>
          </div>
        </div>

        <div class="wd-card-body">
          <!-- Filters -->
          <div class="wd-filters">
            <div class="wd-filter-group">
              <label class="wd-filter-label">
                <i class="bx bx-calendar"></i>
                Year
              </label>
              <select v-model="filters.year" @change="loadDeclarations" class="wd-select">
                <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
            <div class="wd-filter-group">
              <label class="wd-filter-label">
                <i class="bx bx-filter"></i>
                Status
              </label>
              <select v-model="filters.status" @change="loadDeclarations" :disabled="!isApprover && isPsbReader" class="wd-select">
                <option value="">All statuses</option>
                <option value="Draft">Draft</option>
                <option value="Pending">Pending</option>
                <option value="Submitted">Submitted (legacy)</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Overdue">Overdue</option>
              </select>
            </div>
          </div>

          <!-- Workflow Hint -->
          <div class="wd-info-banner">
            <i class="bx bx-info-circle"></i>
            <span>{{ workflowHint }}</span>
          </div>

          <!-- Table -->
          <div class="wd-table-container">
            <table class="wd-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Emp No</th>
                  <th>Designation</th>
                  <th>Year</th>
                  <th>Date Returned</th>
                  <th>Received By</th>
                  <th>Status</th>
                  <th class="wd-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in declarations" :key="d.id" class="wd-table-row">
                  <td class="wd-td-employee">
                    <div class="wd-employee-cell">
                      <div class="wd-employee-avatar">
                        {{ (d.firstName || '').charAt(0) }}{{ (d.lastName || '').charAt(0) }}
                      </div>
                      <span class="wd-employee-name">{{ d.firstName }} {{ d.lastName }}</span>
                    </div>
                  </td>
                  <td class="wd-td-mono">{{ d.emp_no || d.employee_emp_no }}</td>
                  <td>{{ d.designation }}</td>
                  <td class="wd-td-center">{{ d.declaration_year }}</td>
                  <td>{{ d.date_form_returned || '–' }}</td>
                  <td>{{ d.received_by || '–' }}</td>
                  <td>
                    <span class="wd-status-badge" :class="'wd-status-' + (d.status || '').toLowerCase()">
                      {{ d.status }}
                    </span>
                  </td>
                  <td class="wd-td-actions">
                    <div class="wd-action-buttons">
                      <button 
                        v-if="isApprover && allowManualEntry" 
                        type="button" 
                        class="wd-btn-icon wd-btn-edit" 
                        @click="editDeclaration(d)"
                        title="Edit"
                      >
                        <i class="bx bx-edit"></i>
                      </button>
                      <button 
                        v-if="isApprover && ['Pending','Submitted'].indexOf(d.status) >= 0" 
                        type="button" 
                        class="wd-btn-sm wd-btn-success" 
                        @click="approve(d)"
                      >
                        <i class="bx bx-check"></i>
                        Approve
                      </button>
                      <button
                        v-if="isApprover && ['Pending','Submitted'].indexOf(d.status) >= 0"
                        type="button"
                        class="wd-btn-sm wd-btn-danger"
                        @click="rejectFlow(d)"
                      >
                        <i class="bx bx-x"></i>
                        Reject
                      </button>
                      <span v-if="!isApprover" class="wd-read-only-badge">
                        <i class="bx bx-lock-alt"></i>
                        Read-only
                      </span>
                    </div>
                  </td>
                </tr>
                <tr v-if="!declarations.length">
                  <td colspan="8" class="wd-empty-state">
                    <div class="wd-empty-content">
                      <i class="bx bx-folder-open"></i>
                      <p>No declarations found</p>
                      <span>Try adjusting your filters</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Summary Tab -->
      <div v-show="activeTab === 'summary'" class="wd-card">
        <div class="wd-card-header">
          <div class="wd-card-title-group">
            <h3 class="wd-card-title">
              <i class="bx bx-bar-chart-alt-2"></i>
              Summary Statistics
            </h3>
          </div>
          <div class="wd-card-actions">
            <div class="wd-filter-group">
              <label class="wd-filter-label">
                <i class="bx bx-calendar"></i>
                Year
              </label>
              <select v-model="summaryYear" @change="loadSummary" class="wd-select">
                <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="wd-card-body">
          <div class="wd-summary-grid">
            <div class="wd-summary-card wd-summary-card-primary">
              <div class="wd-summary-icon">
                <i class="bx bx-file"></i>
              </div>
              <div class="wd-summary-content">
                <span class="wd-summary-label">Total Declarations</span>
                <span class="wd-summary-value">{{ summary.total }}</span>
              </div>
            </div>
            <div 
              v-for="s in summary.byStatus" 
              :key="s.status" 
              class="wd-summary-card"
              :class="'wd-summary-card-' + (s.status || '').toLowerCase()"
            >
              <div class="wd-summary-icon">
                <i :class="getStatusIcon(s.status)"></i>
              </div>
              <div class="wd-summary-content">
                <span class="wd-summary-label">{{ s.status }}</span>
                <span class="wd-summary-value">{{ s.cnt }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Reports Tab -->
      <div v-show="activeTab === 'reports'" class="wd-card">
        <div class="wd-card-header">
          <div class="wd-card-title-group">
            <h3 class="wd-card-title">
              <i class="bx bx-file"></i>
              Reports
            </h3>
          </div>
        </div>

        <div class="wd-card-body">
          <div class="wd-filters" style="margin-bottom:12px">
            <div class="wd-filter-group">
              <label class="wd-filter-label">Year</label>
              <select v-model="summaryYear" class="wd-select">
                <option v-for="y in yearOptions" :key="'ry'+y" :value="y">{{ y }}</option>
              </select>
            </div>
            <div class="wd-filter-group">
              <label class="wd-filter-label">Search in results</label>
              <input v-model="reportSearch" type="text" class="wd-select" placeholder="Name, emp no, dept…" />
            </div>
          </div>
          <div class="wd-report-actions">
            <button type="button" class="wd-btn wd-btn-primary" @click="runReport('compliance')">
              <i class="bx bx-check-shield"></i>
              Compliance Report
            </button>
            <button type="button" class="wd-btn wd-btn-secondary" @click="runReport('non_compliant')">
              <i class="bx bx-error-circle"></i>
              Non-Compliant Officers
            </button>
          </div>

          <div v-if="filteredReportData.length" class="wd-report-results">
            <div class="wd-report-header">
              <h4 class="wd-report-title">Report Results ({{ filteredReportData.length }})</h4>
              <div class="wd-flex-gap">
                <button type="button" class="wd-btn wd-btn-outline" @click="downloadReport">
                  <i class="bx bx-download"></i>
                  Download CSV
                </button>
                <button type="button" class="wd-btn wd-btn-primary" @click="downloadReportPdf">
                  <i class="bx bx-file"></i>
                  Download PDF
                </button>
              </div>
            </div>
            <div class="wd-table-container">
              <table class="wd-table">
                <thead>
                  <tr>
                    <th v-for="c in reportColumns" :key="c">{{ formatCol(c) }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in filteredReportData" :key="i">
                    <td v-for="c in reportColumns" :key="c">{{ row[c] }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-else class="wd-empty-state">
            <div class="wd-empty-content">
              <i class="bx bx-bar-chart"></i>
              <p>No report generated yet</p>
              <span>Select year and generate a compliance or non-compliant report</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showDeclarationModal" class="modal-overlay" @click.self="showDeclarationModal = false">
        <div class="modal-card modal-card-wide">
          <h3>{{ editingId ? 'Edit' : 'Add' }} wealth declaration</h3>
          <div class="form-group">
            <label>Employee</label>
            <select v-model="form.employee_id" required :disabled="!!editingId">
              <option value="">Select</option>
              <option v-for="emp in employees" :key="emp.id" :value="emp.id">{{ emp.firstName }} {{ emp.lastName }} ({{ emp.empNo }})</option>
            </select>
          </div>
          <div class="form-group">
            <label>Declaration year</label>
            <input v-model.number="form.declaration_year" type="number" min="2020" max="2030">
          </div>
          <div class="form-group">
            <label>Designation</label>
            <input v-model="form.designation" type="text">
          </div>
          <div class="form-group">
            <label>Terms of service</label>
            <input v-model="form.terms_of_service" type="text">
          </div>
          <div class="form-group">
            <label>Date of birth</label>
            <input v-model="form.date_of_birth" type="date">
          </div>
          <div class="form-group">
            <label>Date form returned</label>
            <input v-model="form.date_form_returned" type="date">
          </div>
          <div class="form-group">
            <label>Received by</label>
            <input v-model="form.received_by" type="text">
          </div>
          <div class="form-group">
            <label>Status</label>
            <select v-model="form.status" :disabled="workflowStatusLocked">
              <option value="Draft">Draft</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Overdue">Overdue</option>
            </select>
            <p v-if="workflowStatusLocked" class="field-hint">This file is in workflow—use Approve / Reject on the list.</p>
            <p v-else class="field-hint">Approved / Rejected are normally set from the list actions.</p>
          </div>
          <div class="form-group">
            <label>Remarks</label>
            <input v-model="form.remarks" type="text">
          </div>
          <div v-if="coiaHumanReadable" class="form-group">
            <label>Digital COIA (summary from employee submission)</label>
            <pre class="coia-summary-preview">{{ coiaHumanReadable }}</pre>
          </div>
          <div class="modal-actions">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showDeclarationModal = false">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="saveDeclaration">Save</button>
          </div>
        </div>
      </div>

      <!-- Confirmation Modal -->
      <div v-if="confirmModal.show" class="modal-overlay" @click.self="confirmModal.show = false">
        <div class="modal-card wd-confirm-modal">
          <div class="wd-modal-header">
            <div class="wd-modal-icon wd-modal-icon-confirm">
              <i class="bx bx-help-circle"></i>
            </div>
            <h3 class="wd-modal-title">{{ confirmModal.title }}</h3>
          </div>
          <p class="wd-modal-message">{{ confirmModal.message }}</p>
          <div class="modal-actions">
            <button type="button" class="hr-btn hr-btn-secondary" @click="confirmModal.show = false; confirmModal.reject()">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" @click="confirmModal.show = false; confirmModal.resolve()">Confirm</button>
          </div>
        </div>
      </div>

      <!-- Prompt Modal -->
      <div v-if="promptModal.show" class="modal-overlay" @click.self="promptModal.show = false">
        <div class="modal-card wd-prompt-modal">
          <div class="wd-modal-header">
            <div class="wd-modal-icon wd-modal-icon-prompt">
              <i class="bx bx-edit-alt"></i>
            </div>
            <h3 class="wd-modal-title">{{ promptModal.title }}</h3>
          </div>
          <p v-if="promptModal.message" class="wd-modal-message">{{ promptModal.message }}</p>
          <div class="form-group">
            <textarea 
              v-model="promptModal.value" 
              :placeholder="promptModal.placeholder"
              rows="4"
              class="form-control"
              ref="promptInput"
            ></textarea>
          </div>
          <div class="modal-actions">
            <button type="button" class="hr-btn hr-btn-secondary" @click="promptModal.show = false; promptModal.reject()">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" @click="promptModal.show = false; promptModal.resolve(promptModal.value)">Submit</button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl, toast } from "@/api";
import { downloadCsv, downloadCountyTablePdf } from "@/utils/countyReportExport";

function formatCoiaJsonForDisplay(raw) {
  if (!raw || typeof raw !== "string") return "";
  let o;
  try {
    o = JSON.parse(raw);
  } catch (e) {
    return "";
  }
  if (!o || typeof o !== "object") return "";
  const lines = [];
  const push = (label, v) => {
    if (v != null && String(v).trim() !== "") lines.push(`${label}: ${v}`);
  };
  push("Surname", o.surname);
  push("First name", o.first_name);
  push("Other names", o.other_names);
  push("Date of birth", o.dob);
  push("Place of birth", o.place_of_birth);
  push("Marital status", o.marital_status);
  push("Postal address", o.postal_address);
  push("Physical address", o.physical_address);
  push("Designation", o.designation);
  push("Employer", o.employer_name);
  push("Nature of employment", o.nature_of_employment);
  if (Array.isArray(o.spouses)) {
    o.spouses.forEach((s, i) => {
      const t = [s.surname, s.first_name, s.other_names].filter(Boolean).join(" ");
      if (t) lines.push(`Spouse ${i + 1}: ${t}`);
    });
  }
  if (Array.isArray(o.dependents)) {
    o.dependents.forEach((s, i) => {
      const t = [s.surname, s.first_name, s.other_names].filter(Boolean).join(" ");
      if (t) lines.push(`Dependent ${i + 1}: ${t}`);
    });
  }
  if (Array.isArray(o.financial_statements)) {
    o.financial_statements.forEach((fs, i) => {
      lines.push(`--- Financial statement ${i + 1}: ${fs.subject_label || fs.subject || ""} ---`);
      push("  Statement date", fs.statement_date);
      push("  Income period", [fs.income_period_from, fs.income_period_to].filter(Boolean).join(" → "));
      (fs.income_lines || []).forEach((row) => {
        if (row.description || row.amount) lines.push(`  Income: ${row.description || ""} — ${row.amount || ""}`);
      });
      (fs.asset_lines || []).forEach((row) => {
        if (row.description || row.value) lines.push(`  Asset: ${row.description || ""} — ${row.value || ""}`);
      });
      (fs.liability_lines || []).forEach((row) => {
        if (row.description || row.amount) lines.push(`  Liability: ${row.description || ""} — ${row.amount || ""}`);
      });
    });
  }
  push("Other information", o.other_information);
  push("Witness", o.witness_name);
  push("Witness address", o.witness_address);
  push("Typed signature", o.typed_signature);
  push("Signature date", o.signature_date);
  push("Witness signature", o.witness_signature);
  return lines.length ? lines.join("\n") : "";
}

export default {
  name: "WealthDeclaration",
  props: {
    viewMode: {
      type: String,
      default: "approvals" // approvals | approved
    }
  },
  components: { HrPageLayout },
  computed: {
    coiaHumanReadable() {
      return formatCoiaJsonForDisplay(this.previewFormJson);
    },
    isApprover() { return this.viewMode === "approvals"; },
    isPsbReader() { return this.viewMode === "approved"; },
    roleSectionLabel() {
      return this.isApprover ? "General HR Actions" : "Public Service Board";
    },
    pageTitle() {
      return this.isApprover ? "Wealth-Declaration Approvals" : "Wealth-Declaration-Approved";
    },
    pageSubtitle() {
      return this.isApprover
        ? "Chief Officer (per department): approve or reject employee declarations."
        : "Public Service Board: read-only view of approved declarations.";
    },
    listTabLabel() {
      return this.isApprover ? "Approval Queue" : "Approved Records";
    },
    workflowHint() {
      return this.isApprover
        ? "Employee Self-Service handles submission. As Chief Officer, approve or reject pending declarations."
        : "Read-only list for Public Service Board. Only approved declarations are shown.";
    },
    allowManualEntry() {
      return false;
    },
    workflowStatusLocked() {
      return ["Pending", "Approved", "Rejected"].includes(this.form.status);
    },
    alertClass() {
      return `wd-alert-${this.alertType}`;
    },
    alertIcon() {
      const icons = {
        success: 'bx bx-check-circle',
        error: 'bx bx-error-circle',
        warning: 'bx bx-error',
        info: 'bx bx-info-circle'
      };
      return icons[this.alertType] || icons.info;
    },
    filteredReportData() {
      const q = String(this.reportSearch || "").toLowerCase().trim();
      if (!q) return this.reportData || [];
      return (this.reportData || []).filter((row) =>
        Object.values(row || {}).some((v) => String(v == null ? "" : v).toLowerCase().includes(q))
      );
    }
  },
  data() {
    return {
      activeTab: "list",
      filters: { year: new Date().getFullYear(), status: "" },
      summaryYear: new Date().getFullYear(),
      yearOptions: [],
      declarations: [],
      summary: { total: 0, byStatus: [] },
      employees: [],
      reportData: [],
      reportColumns: [],
      currentReportType: "",
      reportSearch: "",
      showDeclarationModal: false,
      editingId: null,
      saving: false,
      previewFormJson: "",
      // Alert system
      alertMessage: "",
      alertType: "success", // success, error, warning, info
      alertTimeout: null,
      // Confirmation modal
      confirmModal: {
        show: false,
        title: "",
        message: "",
        resolve: () => {},
        reject: () => {}
      },
      // Prompt modal
      promptModal: {
        show: false,
        title: "",
        message: "",
        placeholder: "",
        value: "",
        resolve: () => {},
        reject: () => {}
      },
      form: {
        id: null,
        employee_id: "",
        emp_no: "",
        designation: "",
        terms_of_service: "",
        date_of_birth: "",
        declaration_year: new Date().getFullYear(),
        date_form_returned: "",
        received_by: "",
        remarks: "",
        status: "Pending"
      }
    };
  },
  created() {
    const y = new Date().getFullYear();
    for (let i = y; i >= y - 5; i--) this.yearOptions.push(i);
    if (this.allowManualEntry) this.loadEmployees();
    if (this.isPsbReader && !this.isApprover) this.filters.status = "Approved";
    if (this.isApprover) this.filters.status = "Pending";
    this.loadDeclarations();
  },
  methods: {
    // Status icon helper
    getStatusIcon(status) {
      const icons = {
        'Pending': 'bx bx-time-five',
        'Submitted': 'bx bx-send',
        'Approved': 'bx bx-check-circle',
        'Rejected': 'bx bx-x-circle',
        'Overdue': 'bx bx-error',
        'Draft': 'bx bx-edit'
      };
      return icons[status] || 'bx bx-file';
    },
    // Alert system methods
    showAlert(message, type = 'success', duration = 5000) {
      this.alertMessage = message;
      this.alertType = type;
      if (this.alertTimeout) clearTimeout(this.alertTimeout);
      if (duration > 0) {
        this.alertTimeout = setTimeout(() => {
          this.closeAlert();
        }, duration);
      }
    },
    closeAlert() {
      this.alertMessage = "";
      if (this.alertTimeout) {
        clearTimeout(this.alertTimeout);
        this.alertTimeout = null;
      }
    },
    // Confirmation dialog
    showConfirm(title, message) {
      return new Promise((resolve, reject) => {
        this.confirmModal = {
          show: true,
          title,
          message,
          resolve: () => resolve(true),
          reject: () => reject(false)
        };
      });
    },
    // Prompt dialog
    showPrompt(title, message = "", placeholder = "", defaultValue = "") {
      return new Promise((resolve, reject) => {
        this.promptModal = {
          show: true,
          title,
          message,
          placeholder,
          value: defaultValue,
          resolve,
          reject
        };
        // Focus the input after modal opens
        this.$nextTick(() => {
          if (this.$refs.promptInput) {
            this.$refs.promptInput.focus();
          }
        });
      });
    },
    loadDeclarations() {
      const fd = new FormData();
      fd.append("function", "getWealthDeclarations");
      fd.append("year", this.filters.year);
      const forcedStatus = (!this.isApprover && this.isPsbReader) ? "Approved" : this.filters.status;
      if (forcedStatus) fd.append("status", forcedStatus);
      execute(fd, authUrl).then((res) => {
        this.declarations = (res.data && res.data.success && res.data.data && res.data.data.declarations) ? res.data.data.declarations : [];
      }).catch(() => { this.declarations = []; });
    },
    loadSummary() {
      const fd = new FormData();
      fd.append("function", "getWealthDeclarationSummary");
      fd.append("year", this.summaryYear);
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data) {
          this.summary = { total: res.data.data.total || 0, byStatus: res.data.data.byStatus || [] };
        }
      }).catch(() => {});
    },
    loadEmployees() {
      const fd = new FormData();
      fd.append("function", "getEmployees");
      fd.append("page", "1");
      fd.append("limit", "500");
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.employees) {
          this.employees = res.data.data.employees;
        }
      }).catch(() => {});
    },
    openDeclarationModal(record) {
      this.editingId = record ? record.id : null;
      this.previewFormJson = record && record.form_json ? String(record.form_json) : "";
      if (record) {
        this.form = {
          id: record.id,
          employee_id: record.employee_id,
          emp_no: record.emp_no || record.employee_emp_no,
          designation: record.designation,
          terms_of_service: record.terms_of_service,
          date_of_birth: record.date_of_birth ? record.date_of_birth.slice(0, 10) : "",
          declaration_year: record.declaration_year,
          date_form_returned: record.date_form_returned ? record.date_form_returned.slice(0, 10) : "",
          received_by: record.received_by,
          remarks: record.remarks,
          status: record.status || "Pending"
        };
      } else {
        this.form = {
          id: null,
          employee_id: "",
          emp_no: "",
          designation: "",
          terms_of_service: "",
          date_of_birth: "",
          declaration_year: this.filters.year,
          date_form_returned: "",
          received_by: "",
          remarks: "",
          status: "Pending"
        };
      }
      this.showDeclarationModal = true;
    },
    editDeclaration(d) {
      this.openDeclarationModal(d);
    },
    saveDeclaration() {
      if (!this.form.employee_id) return;
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "saveWealthDeclaration");
      const keys = ["id", "employee_id", "emp_no", "designation", "terms_of_service", "date_of_birth", "declaration_year", "date_form_returned", "received_by", "remarks", "status"];
      keys.forEach((k) => fd.append(k, this.form[k] == null ? "" : this.form[k]));
      execute(fd, authUrl).then((res) => {
        this.saving = false;
        if (res.data && res.data.success) {
          this.showAlert("Declaration saved successfully!", "success");
          this.showDeclarationModal = false;
          this.loadDeclarations();
        } else {
          this.showAlert(res.data && res.data.message ? res.data.message : "Save failed", "error");
        }
      }).catch(() => { 
        this.saving = false;
        this.showAlert("An error occurred while saving", "error");
      });
    },
    workflowCall(name, row, extra) {
      const fd = new FormData();
      fd.append("function", name);
      fd.append("id", row.id);
      if (extra && extra.comments != null) fd.append("comments", extra.comments);
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success) {
          this.showAlert("Action completed successfully!", "success");
          this.loadDeclarations();
        } else {
          this.showAlert(res.data && res.data.message ? res.data.message : "Action failed", "error");
        }
      }).catch(() => {
        this.showAlert("An error occurred", "error");
      });
    },
    async approve(row) {
      try {
        const comments = await this.showPrompt(
          "Approve Declaration",
          "Optional approval comments:",
          "Enter your comments here (optional)..."
        );
        await this.showConfirm(
          "Confirm Approval",
          "Are you sure you want to approve this declaration?"
        );
        this.workflowCall("approveWealthDeclaration", row, { comments: comments || "" });
      } catch (e) {
        // User cancelled
      }
    },
    async rejectFlow(row) {
      try {
        const reason = await this.showPrompt(
          "Reject Declaration",
          "Please provide a reason for rejection (required):",
          "Enter rejection reason...",
          ""
        );
        if (!String(reason).trim()) {
          this.showAlert("A reason is required to reject.", "warning");
          return;
        }
        this.workflowCall("rejectWealthDeclaration", row, { comments: String(reason).trim() });
      } catch (e) {
        // User cancelled
      }
    },
    runReport(type) {
      const fn = type === "compliance" ? "getWealthDeclarationComplianceReport" : "getWealthDeclarationNonCompliantReport";
      const fd = new FormData();
      fd.append("function", fn);
      fd.append("year", this.summaryYear);
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data) {
          this.reportData = res.data.data.data || [];
          this.reportColumns = res.data.data.columns && res.data.data.columns.length ? res.data.data.columns : (this.reportData[0] ? Object.keys(this.reportData[0]) : []);
          this.currentReportType = type === "compliance" ? "wealth_declaration_compliance" : "wealth_declaration_non_compliant";
          this.reportSearch = "";
          if (!this.reportData.length) toast.info("No rows for the selected year");
        } else {
          this.reportData = [];
          toast.error(res.data?.message || "Report failed");
        }
      }).catch(() => {
        this.reportData = [];
        toast.error("Failed to generate report");
      });
    },
    downloadReport() {
      const rows = this.filteredReportData;
      if (!rows.length) return toast.error("No report data");
      const cols = this.reportColumns.length ? this.reportColumns : Object.keys(rows[0] || {});
      downloadCsv(
        cols.map((c) => this.formatCol(c)),
        rows.map((row) => cols.map((c) => row[c])),
        `wealth_${this.currentReportType || "report"}_${this.summaryYear}.csv`
      );
      toast.success("CSV downloaded");
    },
    async downloadReportPdf() {
      const rows = this.filteredReportData;
      if (!rows.length) return toast.error("No report data");
      const cols = this.reportColumns.length ? this.reportColumns : Object.keys(rows[0] || {});
      const title = this.currentReportType === "wealth_declaration_non_compliant"
        ? "WEALTH DECLARATION — NON-COMPLIANT OFFICERS"
        : "WEALTH DECLARATION — COMPLIANCE REPORT";
      try {
        await downloadCountyTablePdf({
          title,
          subtitle: "County Public Service Board",
          periodLabel: `Declaration year ${this.summaryYear}`,
          headers: cols.map((c) => this.formatCol(c)),
          rows: rows.map((row) => cols.map((c) => row[c])),
          filename: `wealth_${this.currentReportType || "report"}_${this.summaryYear}.pdf`,
          orientation: "landscape"
        });
        toast.success("PDF report generated");
      } catch (e) {
        toast.error("PDF export failed");
      }
    },
    formatCol(c) {
      return String(c).replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
    }
  }
};
</script>

<style scoped>
.director-module { padding: 0 0 24px; }

/* ─── Alert Notification System ──────────────────────────── */
.wd-alert {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 320px;
  max-width: 480px;
}

.wd-alert::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  border-radius: 10px 0 0 10px;
}

.wd-alert-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
}

.wd-alert-icon i {
  font-size: 20px;
  line-height: 1;
}

.wd-alert-content {
  flex: 1;
  min-width: 0;
}

.wd-alert-message {
  line-height: 1.5;
  word-wrap: break-word;
}

.wd-alert-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;
  padding: 0;
  margin-left: 4px;
}

.wd-alert-close i {
  font-size: 18px;
  line-height: 1;
}

.wd-alert-close:hover {
  background: rgba(0, 0, 0, 0.08);
}

/* Success Alert */
.wd-alert-success {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  color: #15803d;
  border: 1px solid #86efac;
}

.wd-alert-success::before {
  background: linear-gradient(180deg, #22c55e 0%, #16a34a 100%);
}

.wd-alert-success .wd-alert-icon {
  background: rgba(34, 197, 94, 0.15);
  color: #16a34a;
}

.wd-alert-success .wd-alert-close {
  color: #15803d;
}

.wd-alert-success .wd-alert-close:hover {
  background: rgba(22, 163, 74, 0.15);
}

/* Error Alert */
.wd-alert-error {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  color: #b91c1c;
  border: 1px solid #fca5a5;
}

.wd-alert-error::before {
  background: linear-gradient(180deg, #ef4444 0%, #dc2626 100%);
}

.wd-alert-error .wd-alert-icon {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.wd-alert-error .wd-alert-close {
  color: #b91c1c;
}

.wd-alert-error .wd-alert-close:hover {
  background: rgba(220, 38, 38, 0.15);
}

/* Warning Alert */
.wd-alert-warning {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  color: #92400e;
  border: 1px solid #fcd34d;
}

.wd-alert-warning::before {
  background: linear-gradient(180deg, #f59e0b 0%, #d97706 100%);
}

.wd-alert-warning .wd-alert-icon {
  background: rgba(245, 158, 11, 0.15);
  color: #d97706;
}

.wd-alert-warning .wd-alert-close {
  color: #92400e;
}

.wd-alert-warning .wd-alert-close:hover {
  background: rgba(217, 119, 6, 0.15);
}

/* Info Alert */
.wd-alert-info {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #1e40af;
  border: 1px solid #93c5fd;
}

.wd-alert-info::before {
  background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
}

.wd-alert-info .wd-alert-icon {
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
}

.wd-alert-info .wd-alert-close {
  color: #1e40af;
}

.wd-alert-info .wd-alert-close:hover {
  background: rgba(37, 99, 235, 0.15);
}

/* Alert animations */
.wd-alert-fade-enter-active {
  animation: wd-alert-slide-in 0.3s ease-out;
}

.wd-alert-fade-leave-active {
  animation: wd-alert-slide-out 0.3s ease-in;
}

@keyframes wd-alert-slide-in {
  from {
    opacity: 0;
    transform: translateX(100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes wd-alert-slide-out {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100px);
  }
}

/* ─── Enhanced Modal Styles ──────────────────────────────── */
.wd-confirm-modal,
.wd-prompt-modal {
  max-width: 480px;
}

.wd-modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.wd-modal-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  flex-shrink: 0;
}

.wd-modal-icon i {
  font-size: 28px;
  line-height: 1;
}

.wd-modal-icon-confirm {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
}

.wd-modal-icon-prompt {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #4338ca;
}

.wd-modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}

.wd-modal-message {
  margin: 0 0 16px;
  color: #475569;
  line-height: 1.6;
}

.wd-prompt-modal textarea.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;
}

.wd-prompt-modal textarea.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* ─── Original Styles ──────────────────────────────────────── */

/* ─── Modern UI Enhancements ──────────────────────────────── */

/* Tabs Container */
.wd-tabs-container {
  margin-bottom: 24px;
}

.wd-tabs {
  display: flex;
  gap: 8px;
  background: #f8fafc;
  padding: 6px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.wd-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.wd-tab i {
  font-size: 18px;
}

.wd-tab:hover {
  background: rgba(15, 23, 42, 0.05);
  color: #475569;
}

.wd-tab-active {
  background: #fff;
  color: #0c4a6e;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.wd-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  background: #0c4a6e;
  color: #fff;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 600;
}

.wd-tab-active .wd-tab-badge {
  background: linear-gradient(135deg, #0c4a6e 0%, #0e5a85 100%);
}

/* Card Styles */
.wd-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  margin-bottom: 24px;
}

.wd-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
  border-bottom: 2px solid #e2e8f0;
}

.wd-card-title-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.wd-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0c4a6e;
}

.wd-card-title i {
  font-size: 22px;
}

.wd-badge-count {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: #e0f2fe;
  color: #0c4a6e;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.wd-card-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.wd-card-body {
  padding: 24px;
}

/* Buttons */
.wd-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: 1.5px solid transparent;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.wd-btn i {
  font-size: 18px;
}

.wd-btn-primary {
  background: linear-gradient(135deg, #0c4a6e 0%, #0e5a85 100%);
  color: #fff;
  border-color: #0c4a6e;
}

.wd-btn-primary:hover {
  background: linear-gradient(135deg, #0e5a85 0%, #0c4a6e 100%);
  box-shadow: 0 4px 12px rgba(12, 74, 110, 0.3);
  transform: translateY(-1px);
}

.wd-btn-secondary {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  color: #fff;
  border-color: #475569;
}

.wd-btn-secondary:hover {
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  box-shadow: 0 4px 12px rgba(71, 85, 105, 0.3);
  transform: translateY(-1px);
}

.wd-btn-outline {
  background: #fff;
  color: #0c4a6e;
  border-color: #cbd5e1;
}

.wd-btn-outline:hover {
  background: #f8fafc;
  border-color: #0c4a6e;
}

.wd-btn-sm {
  padding: 6px 12px;
  font-size: 13px;
  border-radius: 6px;
}

.wd-btn-sm i {
  font-size: 16px;
}

.wd-btn-success {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  color: #fff;
  border-color: #16a34a;
}

.wd-btn-success:hover {
  background: linear-gradient(135deg, #15803d 0%, #16a34a 100%);
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3);
}

.wd-btn-danger {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: #fff;
  border-color: #dc2626;
}

.wd-btn-danger:hover {
  background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.wd-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1.5px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.wd-btn-icon i {
  font-size: 16px;
}

.wd-btn-icon:hover {
  background: #f8fafc;
  border-color: #0c4a6e;
  color: #0c4a6e;
}

/* Filters */
.wd-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.wd-filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.wd-filter-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.wd-filter-label i {
  font-size: 16px;
  color: #64748b;
}

.wd-select {
  padding: 8px 12px;
  border: 1.5px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  color: #1e293b;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 160px;
}

.wd-select:hover {
  border-color: #94a3b8;
}

.wd-select:focus {
  outline: none;
  border-color: #0c4a6e;
  box-shadow: 0 0 0 3px rgba(12, 74, 110, 0.1);
}

.wd-select:disabled {
  background: #f1f5f9;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Info Banner */
.wd-info-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
  border-radius: 8px;
  color: #1e40af;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 20px;
}

.wd-info-banner i {
  font-size: 20px;
  flex-shrink: 0;
}

/* Table Styles */
.wd-table-container {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.wd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.wd-table thead {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.wd-table th {
  padding: 14px 16px;
  text-align: left;
  font-weight: 600;
  color: #0c4a6e;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
}

.wd-table tbody tr {
  border-bottom: 1px solid #e2e8f0;
  transition: background-color 0.15s ease;
}

.wd-table tbody tr:hover {
  background: #f8fafc;
}

.wd-table tbody tr:last-child {
  border-bottom: none;
}

.wd-table td {
  padding: 14px 16px;
  color: #334155;
}

.wd-td-center {
  text-align: center;
}

.wd-td-mono {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #0c4a6e;
  font-weight: 600;
}

.wd-th-actions {
  text-align: right;
}

.wd-td-actions {
  text-align: right;
}

/* Employee Cell */
.wd-employee-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.wd-employee-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0c4a6e 0%, #0e5a85 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.wd-employee-name {
  font-weight: 500;
  color: #1e293b;
}

/* Action Buttons */
.wd-action-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.wd-read-only-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #f1f5f9;
  color: #64748b;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
}

.wd-read-only-badge i {
  font-size: 14px;
}

/* Status Badges */
.wd-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.wd-status-pending {
  background: linear-gradient(135deg, #fef3c7 0%, #fde047 100%);
  color: #92400e;
}

.wd-status-submitted {
  background: linear-gradient(135deg, #dbeafe 0%, #93c5fd 100%);
  color: #1e40af;
}

.wd-status-approved {
  background: linear-gradient(135deg, #dcfce7 0%, #86efac 100%);
  color: #15803d;
}

.wd-status-rejected {
  background: linear-gradient(135deg, #fee2e2 0%, #fca5a5 100%);
  color: #991b1b;
}

.wd-status-overdue {
  background: linear-gradient(135deg, #fee2e2 0%, #fca5a5 100%);
  color: #991b1b;
}

.wd-status-draft {
  background: linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%);
  color: #475569;
}

/* Empty State */
.wd-empty-state {
  padding: 60px 24px;
  text-align: center;
}

.wd-empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.wd-empty-content i {
  font-size: 48px;
  color: #cbd5e1;
}

.wd-empty-content p {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
}

.wd-empty-content span {
  font-size: 13px;
  color: #94a3b8;
}

/* Summary Grid */
.wd-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
}

.wd-summary-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #fff;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.wd-summary-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.wd-summary-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 12px;
  flex-shrink: 0;
}

.wd-summary-icon i {
  font-size: 28px;
}

.wd-summary-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.wd-summary-label {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  text-transform: capitalize;
}

.wd-summary-value {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.wd-summary-card-primary .wd-summary-icon {
  background: linear-gradient(135deg, #dbeafe 0%, #93c5fd 100%);
  color: #1e40af;
}

.wd-summary-card-pending .wd-summary-icon {
  background: linear-gradient(135deg, #fef3c7 0%, #fde047 100%);
  color: #92400e;
}

.wd-summary-card-submitted .wd-summary-icon {
  background: linear-gradient(135deg, #dbeafe 0%, #93c5fd 100%);
  color: #1e40af;
}

.wd-summary-card-approved .wd-summary-icon {
  background: linear-gradient(135deg, #dcfce7 0%, #86efac 100%);
  color: #15803d;
}

.wd-summary-card-rejected .wd-summary-icon {
  background: linear-gradient(135deg, #fee2e2 0%, #fca5a5 100%);
  color: #991b1b;
}

.wd-summary-card-overdue .wd-summary-icon {
  background: linear-gradient(135deg, #fee2e2 0%, #fca5a5 100%);
  color: #991b1b;
}

.wd-summary-card-draft .wd-summary-icon {
  background: linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%);
  color: #475569;
}

/* Report Actions */
.wd-report-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
}

.wd-report-results {
  margin-top: 24px;
}

.wd-report-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.wd-report-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

/* Responsive Design */
@media (max-width: 768px) {
  .wd-tabs {
    flex-direction: column;
  }
  
  .wd-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .wd-filters {
    flex-direction: column;
  }
  
  .wd-filter-group {
    width: 100%;
  }
  
  .wd-select {
    width: 100%;
  }
  
  .wd-summary-grid {
    grid-template-columns: 1fr;
  }
  
  .wd-action-buttons {
    flex-direction: column;
    align-items: stretch;
  }
  
  .wd-btn-sm {
    width: 100%;
    justify-content: center;
  }
}

/* ─── Keep Original Styles for Compatibility ──────────────── */
.tabs-row { display: flex; gap: 8px; margin-bottom: 16px; }
.tab-btn { padding: 8px 16px; border: 1px solid #dee2e6; border-radius: 6px; background: #fff; cursor: pointer; }
.tab-btn.active { background: #0d6efd; color: #fff; border-color: #0d6efd; }
.card-section { background: #fff; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); padding: 16px; margin-bottom: 16px; }
.filters-row { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 16px; }
.workflow-hint { font-size: 13px; margin: 0 0 12px; }
.table-wrap { overflow-x: auto; }
.table { width: 100%; font-size: 13px; }
.actions-cell { white-space: normal; }
.actions-cell .hr-btn { margin: 2px 4px 2px 0; }
.badge { padding: 4px 8px; border-radius: 4px; font-size: 12px; }
.badge.status-pending { background: #ffc107; color: #000; }
.badge.status-pending { background: #17a2b8; color: #fff; }
.badge.status-approved { background: #28a745; color: #fff; }
.badge.status-rejected { background: #842029; color: #fff; }
.badge.status-overdue { background: #dc3545; color: #fff; }
.badge.status-draft { background: #6c757d; color: #fff; }
.coia-summary-preview {
  max-height: 220px;
  overflow: auto;
  font-size: 12px;
  line-height: 1.45;
  background: #f8f9fa;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  white-space: pre-wrap;
}
.field-hint { font-size: 12px; color: #6c757d; margin: 6px 0 0; }
.hr-btn-ack { background: #0d6efd; color: #fff; border: none; }
.hr-btn-verify { background: #198754; color: #fff; border: none; }
.hr-btn-reject { background: #dc3545; color: #fff; border: none; }
.modal-card-wide { max-width: 640px; }
.summary-cards { display: flex; flex-wrap: wrap; gap: 16px; }
.summary-card { padding: 16px; background: #f8f9fa; border-radius: 8px; min-width: 120px; }
.summary-card .label { display: block; font-size: 12px; color: #6c757d; }
.summary-card .value { font-size: 1.5rem; font-weight: 600; }
.report-links { display: flex; flex-wrap: wrap; gap: 12px; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-card { background: #fff; border-radius: 8px; padding: 24px; max-width: 480px; width: 90%; max-height: 90vh; overflow-y: auto; }
.form-group { margin-bottom: 12px; }
.form-group label { display: block; font-weight: 600; margin-bottom: 4px; }
.modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }
.mt-2 { margin-top: 16px; }
.text-muted { color: #6c757d; }
</style>
